import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowUpCircle, ArrowDownCircle, Edit, Trash2, ChevronUp, ChevronDown, Clock, CheckCircle, Search, X, CreditCard, ExternalLink } from "lucide-react";
import { Transaction, Category, Conta } from "@/types/database";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getCategoryIcon } from "@/constants/categoryIcons";
import { AdvancedEditTransactionModal } from "@/components/transactions/AdvancedEditTransactionModal";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { useCreditCardOperations } from "@/hooks/useCreditCardOperations";
import { EmptyState } from "../ui/EmptyState";
// Removido uso de data efetiva (vencimento) para exibição na tabela

interface TransactionWithCategory extends Transaction {
  categoria?: Category;
}

interface TransactionTableProps {
  transactions: TransactionWithCategory[];
  selectedDate?: Date;
  onTransactionUpdate?: () => void;
  typeFilter?: 'todos' | 'entrada' | 'saida';
  onTypeFilterChange?: (filter: 'todos' | 'entrada' | 'saida') => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

type SortColumn = 'tipo' | 'descricao' | 'categoria' | 'valor' | 'data' | 'status';
type SortDirection = 'asc' | 'desc';

export function TransactionTable({ transactions, selectedDate = new Date(), onTransactionUpdate, typeFilter = 'todos', onTypeFilterChange, searchQuery = '', onSearchChange }: TransactionTableProps) {
  const navigate = useNavigate();
  const [editingTransaction, setEditingTransaction] = useState<TransactionWithCategory | null>(null);
  const [groupCreditCard, setGroupCreditCard] = useState(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>('data');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionWithCategory | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const perPageOptions = [5, 10, 50, 100, 150, 200, 300];
  ;
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { confirmAndRemoveInstallment, isLoading: isRemovingInstallment } = useCreditCardOperations();
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  // Buscar contas ativas para determinar tipo de conta (cartão de crédito)
  const { data: accountsData } = useQuery<{ id: string; tipo: Conta['tipo']; nome: string; cor: string; dia_fechamento: number | null; dia_vencimento: number | null }[], Error>({
    queryKey: ['contas', 'ativas', 'tipos'],
    queryFn: async (): Promise<{ id: string; tipo: Conta['tipo']; nome: string; cor: string; dia_fechamento: number | null; dia_vencimento: number | null }[]> => {
      const { data, error } = await supabase
        .from('contas')
        .select('id, tipo, nome, cor, dia_fechamento, dia_vencimento')
        .eq('ativo', true);
      if (error) throw error;
      const normalizeTipo = (t: unknown): Conta['tipo'] => {
        switch (t) {
          case 'carteira':
          case 'conta_corrente':
          case 'poupanca':
          case 'cartao_credito':
            return t as Conta['tipo'];
          default:
            return 'conta_corrente';
        }
      };
      const raw = (data ?? []) as Array<{ id: string; tipo?: unknown; nome: string; cor: string; dia_fechamento?: number | null; dia_vencimento?: number | null }>;
      return raw.map((c) => ({
        id: c.id,
        tipo: normalizeTipo(c?.tipo),
        nome: c.nome,
        cor: c.cor,
        dia_fechamento: c.dia_fechamento ?? null,
        dia_vencimento: c.dia_vencimento ?? null,
      }));
    }
  });
  const { data: invoicesData } = useQuery<{ id: string; cartao_id: string; mes_referencia: number; ano_referencia: number; data_pagamento: string | null; data_vencimento: string; paga: boolean; valor_total: number }[], Error>({
    queryKey: ['faturas', 'status-group'],
    queryFn: async (): Promise<{ id: string; cartao_id: string; mes_referencia: number; ano_referencia: number; data_pagamento: string | null; data_vencimento: string; paga: boolean; valor_total: number }[]> => {
      const { data, error } = await supabase
        .from('faturas_cartao')
        .select('id, cartao_id, mes_referencia, ano_referencia, data_pagamento, data_vencimento, paga, valor_total');
      if (error) throw error;
      return (data ?? []) as { id: string; cartao_id: string; mes_referencia: number; ano_referencia: number; data_pagamento: string | null; data_vencimento: string; paga: boolean; valor_total: number }[];
    }
  });
  const accountsById = new Map<string, { tipo: Conta['tipo']; nome: string; cor: string; dia_fechamento: number | null; dia_vencimento: number | null }>((accountsData ?? []).map(c => [c.id, { tipo: c.tipo, nome: c.nome, cor: c.cor, dia_fechamento: c.dia_fechamento, dia_vencimento: c.dia_vencimento }]));
  const invoices = invoicesData ?? [];
  const paidInvoices = invoices.filter((invoice) => invoice.paga);
  const isCreditCardTransaction = (transaction: TransactionWithCategory) => accountsById.get(transaction.conta_id || '')?.tipo === 'cartao_credito';
  const isSameMonth = (dateString: string | null | undefined, date: Date) => {
    if (!dateString) return false;
    const parts = String(dateString).split('T')[0].split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return false;
    return parts[0] === date.getFullYear() && parts[1] === date.getMonth() + 1;
  };
  const getInvoiceReferenceForTransaction = (transaction: TransactionWithCategory) => {
    if (!transaction.conta_id || !transaction.data) return null;
    const account = accountsById.get(transaction.conta_id);
    if (!account || account.tipo !== 'cartao_credito') return null;

    const parts = String(transaction.data).split('T')[0].split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;

    const transactionDate = new Date(parts[0], parts[1] - 1, parts[2]);
    let mes = transactionDate.getMonth() + 1;
    let ano = transactionDate.getFullYear();
    const ultimoDiaMes = (y: number, m: number) => new Date(y, m, 0).getDate();

    if (account.dia_fechamento != null) {
      const diaFechamentoAjustado = Math.min(account.dia_fechamento, ultimoDiaMes(ano, mes));
      const dataFechamento = new Date(ano, mes - 1, diaFechamentoAjustado);
      if (transactionDate > dataFechamento) {
        if (mes === 12) {
          mes = 1;
          ano += 1;
        } else {
          mes += 1;
        }
      }
    } else if (account.dia_vencimento != null) {
      const diaVencimentoAjustado = Math.min(account.dia_vencimento, ultimoDiaMes(ano, mes));
      const dataVencimento = new Date(ano, mes - 1, diaVencimentoAjustado);
      if (transactionDate > dataVencimento) {
        if (mes === 12) {
          mes = 1;
          ano += 1;
        } else {
          mes += 1;
        }
      }
    }

    return { cartao_id: transaction.conta_id, mes_referencia: mes, ano_referencia: ano };
  };
  const getPaidInvoiceForTransaction = (transaction: TransactionWithCategory) => {
    const ref = getInvoiceReferenceForTransaction(transaction);
    if (!ref) return null;
    return paidInvoices.find((invoice) =>
      invoice.cartao_id === ref.cartao_id &&
      invoice.mes_referencia === ref.mes_referencia &&
      invoice.ano_referencia === ref.ano_referencia
    ) || null;
  };
  const isTransactionPaid = (transaction: TransactionWithCategory) => {
    if (transaction.foi_paga) return true;
    if (!isCreditCardTransaction(transaction)) return false;
    return Boolean(getPaidInvoiceForTransaction(transaction));
  };
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const onlyDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    const hasTimePart = /T\d{2}:\d{2}:\d{2}/.test(dateString);
    if (onlyDateMatch) {
      const [y, m, d] = dateString.split('-').map(Number);
      const utc = new Date(Date.UTC(y, m - 1, d));
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      }).format(utc);
    }
    if (hasTimePart) {
      const dt = new Date(dateString);
      const normalizedUtcDay = new Date(
        dt.getUTCFullYear(),
        dt.getUTCMonth(),
        dt.getUTCDate()
      );
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(normalizedUtcDay);
    }
    const fallback = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(fallback);
  };

  // Determinar a data efetiva para exibição/sort
  const getEffectiveDateRaw = (transaction: TransactionWithCategory) => {
    if (transaction.id.startsWith('grouped-cc') && transaction.foi_paga && transaction.data_pagamento) {
      return transaction.data_pagamento;
    }
    const isParcel = Boolean(transaction.transacao_pai_id) || (transaction.descricao && transaction.descricao.match(/\(\d+\/\d+\)/));
    if (isCreditCardTransaction(transaction) && isParcel) {
      return transaction.criado_em || transaction.data;
    }
    return transaction.data || transaction.criado_em;
  };

  const formatEffectiveDate = (transaction: TransactionWithCategory) => {
    const rawDate = getEffectiveDateRaw(transaction);
    return formatDate(rawDate);
  };

  const formatDescription = (transaction: TransactionWithCategory) => {
    // Para transações parceladas/recorrentes, remover a numeração da descrição
    // pois ela será exibida no badge separadamente
    if (transaction.descricao && transaction.descricao.match(/\(\d+\/\d+\)/)) {
      return transaction.descricao.replace(/\s*\(\d+\/\d+\)\s*$/, '');
    }
    return transaction.descricao;
  };

  // Função para verificar se é uma transação parcelada
  const isInstallmentTransaction = (transaction: TransactionWithCategory) => {
    // Verifica se a descrição contém um padrão de parcela (X/Y) ou se tem no banco
    return (transaction.descricao && transaction.descricao.match(/\(\d+\/\d+\)/)) || 
           transaction.numero_parcela || 
           (transaction.compras_parceladas && transaction.compras_parceladas.length > 0);
  };

  // Função para exibir o badge de parcela
  const renderInstallmentBadge = (transaction: TransactionWithCategory) => {
    if (!isInstallmentTransaction(transaction)) return null;

    let numeroParcela: string | number = '';
    let totalParcelas: string | number = '';

    // Prioridade 1: Dados do banco
    if (transaction.numero_parcela || transaction.total_parcelas) {
      numeroParcela = transaction.numero_parcela || 1;
      totalParcelas = transaction.total_parcelas || '?';
    } 
    // Prioridade 2: Array de compras parceladas (primeira parcela/origem)
    else if (transaction.compras_parceladas && transaction.compras_parceladas.length > 0) {
      numeroParcela = transaction.compras_parceladas[0].parcela_atual || 1;
      totalParcelas = transaction.compras_parceladas[0].numero_parcelas || '?';
    }
    // Prioridade 3: Extrair da descrição (legado)
    else if (transaction.descricao) {
      const match = transaction.descricao.match(/\((\d+)\/(\d+)\)/);
      if (match) {
        numeroParcela = match[1];
        totalParcelas = match[2];
      }
    }

    if (!numeroParcela && !totalParcelas) return null;

    return (
      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full whitespace-nowrap">
        {numeroParcela}/{totalParcelas}
      </span>
    );
  };

  const getStatusInfo = (transaction: TransactionWithCategory) => {
    // Verifica se a transação foi paga
    if (transaction.foi_paga) {
      return {
        text: "Efetuada",
        icon: <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
        variant: "outline" as const,
        className: "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20"
      };
    }

    // Se não foi paga, mostrar como pendente
    return {
      text: "Pendente",
      icon: <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
      variant: "outline" as const,
      className: "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"
    };
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' ?
      <ChevronUp className="h-4 w-4" /> :
      <ChevronDown className="h-4 w-4" />;
  };

  useEffect(() => {
    // reset to first page when page size or data changes
    setCurrentPage(1);
  }, [rowsPerPage, transactions.length]);

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const add = (p: number | 'ellipsis') => pages.push(p);
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) add(i);
    } else {
      add(1);
      if (safeCurrentPage > 3) add('ellipsis');
      const start = Math.max(2, safeCurrentPage - 1);
      const end = Math.min(totalPages - 1, safeCurrentPage + 1);
      for (let i = start; i <= end; i++) add(i);
      if (safeCurrentPage < totalPages - 2) add('ellipsis');
      add(totalPages);
    }
    return pages;
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };

  const handleChangeRows = (value: string) => {
    const size = parseInt(value, 10);
    if (!Number.isNaN(size)) setRowsPerPage(size);
  };

  const handleRowClick = (transaction: TransactionWithCategory) => {
    // Allow click to edit only on mobile and tablet (screens smaller than desktop)
    if (isMobile) {
      if (transaction.id.startsWith('grouped-cc') || isCreditCardTransaction(transaction)) {
        toast.info("Transações de cartão de crédito devem ser gerenciadas pela fatura");
        return;
      }
      setEditingTransaction(transaction);
    }
  };

  const handleLongPressStart = (transaction: TransactionWithCategory) => {
    if (isMobile) {
      if (transaction.id.startsWith('grouped-cc') || isCreditCardTransaction(transaction)) {
        return;
      }
      longPressTimer.current = setTimeout(() => {
        setSelectedTransaction(transaction);
        setShowActionSheet(true);
      }, 500);
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleSheetEdit = () => {
    if (selectedTransaction) {
      setEditingTransaction(selectedTransaction);
      setShowActionSheet(false);
    }
  };

  const handleSheetDelete = () => {
    if (selectedTransaction) {
      handleDelete(selectedTransaction.id, false);
      setShowActionSheet(false);
    }
  };

  const handleDelete = async (transactionId: string, deleteAll: boolean = false) => {
    // Fechar o modal de edição se estiver aberto para evitar conflito de z-index
    if (editingTransaction?.id === transactionId) {
      setEditingTransaction(null);
    }
    try {
      // Buscar informações da transação e conta para determinar se é cartão de crédito parcelado
      const { data: transactionData, error: transactionError } = await supabase
        .from("transacoes")
        .select(`
          *,
          contas!inner(tipo)
        `)
        .eq("id", transactionId)
        .single();

      if (transactionError) throw transactionError;

      // Verificar se existe registro de compra parcelada para esta transação
      const { data: compraParcelada } = await supabase
        .from("compras_parceladas")
        .select("numero_parcelas")
        .eq("transacao_origem_id", transactionId)
        .maybeSingle();

      // Verificar se é pai de alguma transação
      const { count: filhosCount } = await supabase
        .from("transacoes")
        .select("id", { count: "exact", head: true })
        .eq("transacao_pai_id", transactionId);

      const hasChildren = (filhosCount || 0) > 0;
      const isInstallmentExpense = !!compraParcelada || hasChildren;
      const isCreditCard = transactionData?.contas?.tipo === 'cartao_credito';

      // Se é uma despesa parcelada de cartão de crédito, usar a lógica correta
      if (isCreditCard && (isInstallmentExpense || transactionData.transacao_pai_id)) {
        if (deleteAll) {
          // Usar ID da transação pai se for uma parcela, ou o próprio ID se for a origem
          const originId = transactionData.transacao_pai_id || transactionId;
          const parcelas = (isInstallmentExpense && compraParcelada?.numero_parcelas) ||
            transactionData.numero_total_repeticoes || 1;

          const success = await confirmAndRemoveInstallment(
            originId,
            transactionData.descricao,
            transactionData.valor,
            parcelas,
            true // Skip window.confirm since they already confirmed in AlertDialog
          );

          if (success) {
            // Clear the editing transaction state if the deleted transaction was being edited
            if (editingTransaction?.id === transactionId) {
              setEditingTransaction(null);
            }
            onTransactionUpdate?.();
          }
        } else {
          // Deletar (ignorar) apenas esta parcela específica
          const { error: ignoreError } = await supabase
            .from("transacoes")
            .update({ ignorar_transacao: true })
            .eq("id", transactionId);
            
          if (ignoreError) throw ignoreError;
          
          toast.success("Parcela excluída com sucesso!");
          onTransactionUpdate?.();
        }
        return;
      }

      // Para outras transações, usar o método tradicional
      const { error } = await supabase
        .from("transacoes")
        .delete()
        .eq("id", transactionId);

      if (error) throw error;

      toast.success("Transação excluída com sucesso!");

      // Clear the editing transaction state if the deleted transaction was being edited
      if (editingTransaction?.id === transactionId) {
        setEditingTransaction(null);
      }

      onTransactionUpdate?.();
    } catch (error) {
      console.error("Erro ao excluir transação:", error);
      toast.error("Erro ao excluir transação");
    }
  };

  // Filter out ignored transactions
  let filteredTransactions = transactions.filter(transaction => !transaction.ignorar_transacao);

  if (groupCreditCard) {
    const ccTransactions = filteredTransactions.filter(isCreditCardTransaction);
    const nonCcTransactions = filteredTransactions.filter(t => !isCreditCardTransaction(t));
    
    if (ccTransactions.length > 0) {
      if (!searchQuery.trim()) {
        const invoiceRows = invoices
          .filter((invoice) => {
            const account = accountsById.get(invoice.cartao_id);
            if (!account || account.tipo !== 'cartao_credito') return false;
            if (invoice.paga) return isSameMonth(invoice.data_pagamento, selectedDate);
            return isSameMonth(invoice.data_vencimento, selectedDate);
          })
          .map((invoice) => {
            const account = accountsById.get(invoice.cartao_id);
            return {
              id: `grouped-cc-${invoice.id}`,
              descricao: `Fatura: ${account?.nome || 'Cartão'}`,
              valor: Number(invoice.valor_total || 0),
              tipo: 'saida',
              data: invoice.data_vencimento,
              criado_em: invoice.data_vencimento,
              data_pagamento: invoice.data_pagamento,
              foi_paga: invoice.paga,
              conta_id: invoice.cartao_id,
              categoria: {
                nome: 'Cartões',
                cor: account?.cor || '#6366f1',
                icone: 'card'
              },
              fatura_id: invoice.id
            } as unknown as TransactionWithCategory;
          });

        filteredTransactions = [...nonCcTransactions, ...invoiceRows];
      } else {
      const groups = ccTransactions.reduce((acc, t) => {
        // Agrupar separadamente as transações pagas e pendentes para evitar 
        // misturar faturas de meses anteriores pagas no mês atual com a fatura atual pendente
        const statusKey = isTransactionPaid(t) ? 'paid' : 'pending';
        const id = `${t.conta_id || 'unknown'}-${statusKey}`;
        if (!acc[id]) acc[id] = [];
        acc[id].push(t);
        return acc;
      }, {} as Record<string, TransactionWithCategory[]>);

      const groupedEntries = Object.entries(groups).map(([groupId, ts]) => {
        const sum = ts.reduce((acc, t) => acc + Number(t.valor), 0);
        // Extrair o contaId original do groupId (ex: "uuid-paid" -> "uuid")
        const contaId = groupId.substring(0, groupId.lastIndexOf('-'));
        const account = accountsById.get(contaId);
        
        // Como agrupamos por status, todas as transações no grupo têm o mesmo status
        const isFullyPaid = ts.length > 0 && ts.every(isTransactionPaid);
        
        // Se a fatura está paga, use a data de pagamento da primeira transação como data principal,
        // senão use a data de vencimento da fatura se houver (por simplificação, usamos a data da primeira transação se não houver)
        const paymentDate = isFullyPaid
          ? ts
              .map(t => t.data_pagamento || getPaidInvoiceForTransaction(t)?.data_pagamento || null)
              .find((d): d is string => Boolean(d))
          : undefined;
        
        return {
          id: `grouped-cc-${groupId}`,
          descricao: `Fatura: ${account?.nome || 'Cartão'}`,
          valor: sum,
          tipo: 'saida',
          data: paymentDate || ts[0].data,
          criado_em: ts[0].criado_em,
          data_pagamento: paymentDate,
          foi_paga: isFullyPaid,
          conta_id: contaId,
          categoria: {
            nome: 'Cartões',
            cor: account?.cor || '#6366f1',
            icone: 'card'
          }
        } as unknown as TransactionWithCategory;
      });
      
        filteredTransactions = [...nonCcTransactions, ...groupedEntries];
      }
    }
  }

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    // Sempre manter os cartões agrupados no topo quando o filtro estiver ativo
    if (groupCreditCard) {
      const aIsGrouped = a.id.startsWith('grouped-cc');
      const bIsGrouped = b.id.startsWith('grouped-cc');
      
      if (aIsGrouped && !bIsGrouped) return -1;
      if (!aIsGrouped && bIsGrouped) return 1;
    }

    let aValue: string | number | Date = a[sortColumn];
    let bValue: string | number | Date = b[sortColumn];

    // Handle special cases
    if (sortColumn === 'categoria') {
      aValue = a.categoria?.nome || '';
      bValue = b.categoria?.nome || '';
    } else if (sortColumn === 'status') {
      aValue = a.foi_paga ? 'efetuada' : 'pendente';
    }

    // Convert to comparable values
    if (sortColumn === 'data') {
      // Ordenar pela mesma regra de exibição: parcelas de cartão usam criado_em (data da compra)
      const parseDateComparable = (raw: string): number => {
        const onlyDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(raw);
        const hasTimePart = /T\d{2}:\d{2}:\d{2}/.test(raw);
        if (onlyDateMatch) {
          const [y, m, d] = raw.split('-').map(Number);
          return new Date(Date.UTC(y, m - 1, d)).getTime();
        }
        if (hasTimePart) {
          const d = new Date(raw);
          return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()).getTime();
        }
        return new Date(String(raw)).getTime();
      };
      const aRaw = String(getEffectiveDateRaw(a));
      const bRaw = String(getEffectiveDateRaw(b));
      aValue = parseDateComparable(aRaw);
      bValue = parseDateComparable(bRaw);
    } else if (sortColumn === 'valor') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    } else {
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
    }

    // Compare values
    let comparison = 0;
    if (aValue > bValue) comparison = 1;
    if (aValue < bValue) comparison = -1;

    return sortDirection === 'desc' ? -comparison : comparison;
  });

  // Pagination calculations with filtered data
  const totalItems = sortedTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const visibleTransactions = sortedTransactions.slice(startIndex, endIndex);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">
            {onTypeFilterChange && (!searchOpen || !isMobile) && (
              <div className="flex gap-2 items-center flex-wrap">
                <Button
                  variant={typeFilter === 'entrada' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onTypeFilterChange(typeFilter === 'entrada' ? 'todos' : 'entrada')}
                  className="gap-2 px-2 sm:px-4"
                >
                  <ArrowUpCircle className="h-4 w-4" />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Receitas</span>
                </Button>
                <Button
                  variant={typeFilter === 'saida' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onTypeFilterChange(typeFilter === 'saida' ? 'todos' : 'saida')}
                  className="gap-2 px-2 sm:px-4"
                >
                  <ArrowDownCircle className="h-4 w-4" />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Despesas</span>
                </Button>
                <Button
                  variant={groupCreditCard ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setGroupCreditCard(!groupCreditCard)}
                  className="gap-2 px-2 sm:px-4"
                >
                  <CreditCard className="h-4 w-4" />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Agrupar Cartões</span>
                </Button>
              </div>
            )}

            {/* Campo de busca alinhado */}
            <div className={`flex items-center gap-2 ${searchOpen && isMobile ? 'w-full' : ''}`}>
              {searchOpen && onSearchChange ? (
                <div className="flex items-center gap-2 w-full animate-in fade-in slide-in-from-right-4 duration-200">
                  <div className="relative flex-1">
                    <Input
                      value={searchQuery}
                      onChange={(e) => onSearchChange(e.target.value)}
                      placeholder="Buscar..."
                      className="pr-8 h-9"
                      autoFocus
                    />
                    {searchQuery && searchQuery.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (onSearchChange) {
                            onSearchChange('');
                          }
                        }}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
                        aria-label="Limpar busca"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchOpen(false);
                      if (onSearchChange) {
                        onSearchChange('');
                      }
                    }}
                    aria-label="Fechar busca"
                    className="flex-shrink-0 h-9 w-9 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant={searchQuery && searchQuery.length > 0 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Pesquisar transações"
                  className={`flex-shrink-0 h-9 w-9 p-0 sm:w-auto sm:px-3 ${searchQuery && searchQuery.length > 0 ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                >
                  <Search className="h-4 w-4" />
                  {searchQuery && searchQuery.length > 0 && (
                    <span className="hidden sm:inline ml-1 text-xs bg-white text-blue-600 px-1 rounded">
                      {searchQuery.length > 10 ? `${searchQuery.substring(0, 10)}...` : searchQuery}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="py-12 flex justify-center border rounded-md">
              <EmptyState
                icon={searchQuery ? Search : ArrowDownCircle}
                title={searchQuery ? "Nenhuma transação encontrada" : "Sem transações"}
                description={searchQuery
                  ? `Não encontramos resultados para "${searchQuery}". Tente outros termos ou filtros.`
                  : "Sua lista de transações está vazia. Adicione sua primeira despesa ou receita!"
                }
                actionLabel={searchQuery ? "Limpar busca" : undefined}
                onAction={searchQuery ? () => onSearchChange?.("") : undefined}
              />
            </div>
          ) : isMobile ? (
            <div className="space-y-3">
              {visibleTransactions.map((transaction, index) => (
                <div
                  key={`${transaction.id}-${transaction.data}-${index}`}
                  className="bg-card border border-border rounded-xl p-4 shadow-soft hover:bg-accent/30 hover:shadow-medium transition-all duration-300 cursor-pointer active:scale-[0.98]"
                  onClick={() => handleRowClick(transaction)}
                  onTouchStart={() => handleLongPressStart(transaction)}
                  onTouchEnd={handleLongPressEnd}
                  onTouchCancel={handleLongPressEnd}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {/* Icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      transaction.tipo === 'entrada' ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}>
                      {transaction.categoria ? (
                        (() => {
                          const Icon = getCategoryIcon(transaction.categoria!.icone);
                          return <Icon className="w-4 h-4" style={{ color: transaction.categoria!.cor }} />;
                        })()
                      ) : (
                        transaction.tipo === 'entrada' ?
                          <ArrowUpCircle className="w-4 h-4 text-green-600" /> :
                          <ArrowDownCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>

                    {/* Description & Category */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-foreground line-clamp-1 break-all mb-0.5">{formatDescription(transaction)}</h3>
                      <p className="text-[10px] text-muted-foreground line-clamp-1">{transaction.categoria?.nome || 'Sem categoria'}</p>
                    </div>

                    {/* Value */}
                    <div className={`text-right flex-shrink-0 font-semibold text-sm whitespace-nowrap ml-2 ${
                      transaction.tipo === "entrada" ? "text-income" : "text-expense"
                    }`}>
                      {formatCurrency(transaction.valor)}
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <div className="flex items-center gap-3">
                      {/* Date */}
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{formatEffectiveDate(transaction)}</span>
                      </div>

                      {/* Installments */}
                      {isInstallmentTransaction(transaction) && (
                        <div className="flex items-center -ml-1">
                          {renderInstallmentBadge(transaction)}
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex items-center gap-1.5">
                      {getStatusInfo(transaction).icon}
                      <span className="text-[10px] font-medium text-muted-foreground">{getStatusInfo(transaction).text}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('tipo')}
                    >
                      <div className="flex items-center gap-1">
                        Tipo
                        {getSortIcon('tipo')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('descricao')}
                    >
                      <div className="flex items-center gap-1">
                        Descrição
                        {getSortIcon('descricao')}
                      </div>
                    </TableHead>
                    {!isTablet && (
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('categoria')}
                      >
                        <div className="flex items-center gap-1">
                          Categoria
                          {getSortIcon('categoria')}
                        </div>
                      </TableHead>
                    )}
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('valor')}
                    >
                      <div className="flex items-center gap-1">
                        Valor
                        {getSortIcon('valor')}
                      </div>
                    </TableHead>
                    {!isTablet && (
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-1">
                          Status
                          {getSortIcon('status')}
                        </div>
                      </TableHead>
                    )}
                    {!isTablet && (
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('data')}
                      >
                        <div className="flex items-center gap-1">
                          Data
                          {getSortIcon('data')}
                        </div>
                      </TableHead>
                    )}
                    <TableHead className="w-[100px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleTransactions.map((transaction, index) => (
                    <TableRow
                      key={`${transaction.id}-${transaction.data}-${index}`}
                      className="cursor-pointer select-none active:bg-muted/50 hover:bg-muted/50 min-h-[44px] md:cursor-default md:hover:bg-muted/50"
                      onClick={() => handleRowClick(transaction)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {transaction.tipo === "entrada" ? (
                            <ArrowUpCircle className="h-4 w-4 text-income" />
                          ) : (
                            <ArrowDownCircle className="h-4 w-4 text-expense" />
                          )}
                          <span className="capitalize">{transaction.tipo === "entrada" ? "Receita" : "Despesa"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span>{formatDescription(transaction)}</span>
                            {renderInstallmentBadge(transaction)}
                          </div>
                        </div>
                      </TableCell>
                      {!isTablet && (
                        <TableCell>
                          {transaction.categoria ? (
                            <Badge variant="outline" className="gap-1">
                              {(() => {
                                const Icon = getCategoryIcon(transaction.categoria!.icone);
                                return <Icon className="w-3 h-3" style={{ color: transaction.categoria!.cor }} />;
                              })()}
                              {transaction.categoria.nome}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">Sem categoria</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className={
                        `text-amount-sm tabular-nums font-medium ${transaction.tipo === "entrada" ? "text-income" : "text-expense"}`
                      }>
                        {formatCurrency(transaction.valor)}
                      </TableCell>
                      {!isTablet && (
                        <TableCell>
                          <Badge variant={getStatusInfo(transaction).variant} className={`gap-1 ${getStatusInfo(transaction).className || ''}`}>
                            {getStatusInfo(transaction).icon}
                            {getStatusInfo(transaction).text}
                          </Badge>
                        </TableCell>
                      )}
                      {!isTablet && (
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">{formatEffectiveDate(transaction)}</span>
                            {transaction.foi_paga && transaction.data_pagamento && getStatusInfo(transaction).text === "Efetuada" && (
                              <span className="text-xs text-muted-foreground">
                                Pago: {formatDate(transaction.data_pagamento)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        {accountsData && !transaction.id.startsWith('grouped-cc') && !isCreditCardTransaction(transaction) ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTransaction(transaction);
                              }}
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              aria-label="Editar transação"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  aria-label="Excluir transação"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Fechar o modal de edição antes de abrir o AlertDialog
                                    if (editingTransaction?.id === transaction.id) {
                                      setEditingTransaction(null);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="z-[60]">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir transação</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja excluir esta transação?
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    {(transaction.id.startsWith('grouped-cc') || isCreditCardTransaction(transaction)) ? null : (
                                      ((((transaction as unknown as { compras_parceladas?: unknown[] }).compras_parceladas?.length ?? 0) > 0 || transaction.transacao_pai_id) || transaction.numero_parcela || transaction.total_parcelas) ? (
                                      <>
                                        <AlertDialogAction
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(transaction.id, false);
                                          }}
                                          className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
                                        >
                                          Apenas esta parcela
                                        </AlertDialogAction>
                                        <AlertDialogAction
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(transaction.id, true);
                                          }}
                                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Todas as parcelas
                                        </AlertDialogAction>
                                      </>
                                    ) : (
                                      <AlertDialogAction
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(transaction.id, false);
                                        }}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Excluir
                                      </AlertDialogAction>
                                    )
                                  )}
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ) : (
                          accountsData && isCreditCardTransaction(transaction) ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex justify-start w-full">
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20" 
                                        aria-label="Ver fatura do cartão"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate('/', { 
                                            state: { 
                                              tab: 'credit-cards', 
                                              cardId: transaction.conta_id,
                                              targetDate: transaction.data,
                                              invoiceId: (transaction as unknown as { fatura_id?: string }).fatura_id
                                            } 
                                          });
                                        }}
                                      >
                                      <ExternalLink className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  <p className="text-xs">Gerenciar na fatura do cartão</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : null
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination footer */}
          <div className="mt-4 flex flex-col gap-3">
            {/* Stats */}
            <div className="text-sm text-muted-foreground text-center">
              Mostrando {totalItems === 0 ? 0 : startIndex + 1}–{endIndex} de {totalItems}
            </div>

            {/* Responsive pagination */}
            <div className="flex flex-col gap-3">
              {/* Mobile: Simplified Previous/Next only */}
              <div className="flex items-center justify-center gap-2 sm:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(safeCurrentPage - 1)}
                  disabled={safeCurrentPage <= 1}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  {safeCurrentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(safeCurrentPage + 1)}
                  disabled={safeCurrentPage >= totalPages}
                >
                  Próximo
                </Button>
              </div>

              {/* Tablet/Desktop: Full pagination with responsive controls */}
              <div className="hidden sm:flex sm:flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                {/* Items per page */}
                <div className="flex items-center justify-center lg:justify-start gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Linhas por página</span>
                  <Select value={String(rowsPerPage)} onValueChange={handleChangeRows}>
                    <SelectTrigger className="h-8 w-20 justify-center">
                      <SelectValue placeholder={rowsPerPage} />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-popover">
                      {perPageOptions.map((opt) => (
                        <SelectItem key={opt} value={String(opt)} className="justify-center">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pagination */}
                <div className="flex justify-center">
                  <Pagination>
                    <PaginationContent className="flex-wrap justify-center">
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (safeCurrentPage > 1) handleChangePage(safeCurrentPage - 1);
                          }}
                          className={safeCurrentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                      {getPageNumbers().slice(0, window.innerWidth >= 1024 ? undefined : 5).map((p, idx) => (
                        p === 'ellipsis' ? (
                          <PaginationItem key={`el-${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={p}>
                            <PaginationLink
                              href="#"
                              isActive={p === safeCurrentPage}
                              onClick={(e) => {
                                e.preventDefault();
                                handleChangePage(p as number);
                              }}
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      ))}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (safeCurrentPage < totalPages) handleChangePage(safeCurrentPage + 1);
                          }}
                          className={safeCurrentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </div>

              {/* Mobile items per page selector */}
              <div className="flex items-center justify-center gap-2 sm:hidden">
                <span className="text-sm text-muted-foreground">Itens por página:</span>
                <Select value={String(rowsPerPage)} onValueChange={handleChangeRows}>
                  <SelectTrigger className="h-8 w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {perPageOptions.map((opt) => (
                      <SelectItem key={opt} value={String(opt)}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>

      </Card>

      {/* Action Sheet for Mobile */}
      <Sheet open={showActionSheet} onOpenChange={setShowActionSheet}>
        <SheetContent side="bottom" className="rounded-t-lg">
          <SheetHeader>
            <SheetTitle>Ações da Transação</SheetTitle>
            <SheetDescription>
              {selectedTransaction?.descricao}
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-3 py-4">
            <Button
              onClick={handleSheetEdit}
              className="flex items-center gap-2 justify-start"
              variant="ghost"
            >
              <Edit className="h-4 w-4" />
              Editar Transação
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 justify-start text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Fechar o modal de edição antes de abrir o AlertDialog
                    if (editingTransaction?.id === selectedTransaction?.id) {
                      setEditingTransaction(null);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir Transação
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="z-[60]">
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir transação</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir esta transação?
                    Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={(e) => {
                    e.stopPropagation();
                    handleSheetDelete();
                  }}>
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </SheetContent>
      </Sheet>

      {editingTransaction && (
        <AdvancedEditTransactionModal
          transaction={editingTransaction}
          isOpen={!!editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onSuccess={() => {
            setEditingTransaction(null);
            onTransactionUpdate?.();
          }}
        />
      )}
    </>
  );
}
